import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';

import { useModal } from 'core/modules/Modal/ModalProvider';
import { fetchMerchantRelatedBonus, fetchBonus } from 'core/actions/bonus';

import { selectUserIsIdentified } from 'core/selectors/user';
import {
  selectMerchantBonusOffersToDisplay, selectBonusOffersConfig, selectBonusOffersIsLoading, selectBonusOffersIsLoaded,
} from 'core/modules/BonusOffers/selectors';

import { sendAnalyticsBonusViewAction } from '../../actions';

import './MerchantBonusOffer.scss';

function MerchantBonusOffer() {
  const config = useSelector(selectBonusOffersConfig);
  const isLoading = useSelector(selectBonusOffersIsLoading);
  const isLoaded = useSelector(selectBonusOffersIsLoaded);
  const bonusOffer = useSelector(selectMerchantBonusOffersToDisplay);
  const userIsIdentified = useSelector(selectUserIsIdentified);
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const prevIsLoaded = useRef(isLoaded);
  const getMerchantBonus = () => {
    if (!isLoaded && !isLoading) {
      const { api: { params, options } } = config;
      if (userIsIdentified) {
        dispatch(fetchMerchantRelatedBonus(params, options));
      } else {
        dispatch(fetchBonus(params, options));
      }
    }
  };

  const clickHandler = () => {
    const { terms } = bonusOffer;
    openModal({
      name: 'mt_termsAndConditions',
      params: { terms },
    });
  };

  useEffect(() => {
    getMerchantBonus();
  }, []);

  useEffect(() => {
    if (bonusOffer && !prevIsLoaded.current && isLoaded) {
      prevIsLoaded.current = isLoaded;
      dispatch(sendAnalyticsBonusViewAction(bonusOffer));
    }
  }, [isLoaded]);

  if (!bonusOffer) return null;

  const {
    bonusTag, heading, spent, total, bonusEndInfo, clickUrl,
    hasReachedTotal, buttonTitle, id, merchantName, offerDetails,
  } = bonusOffer;

  return (
    <div
      className="mn_merchantBonusDetails"
      data-id={id}
      data-merchant-name={merchantName}
      data-offer-description={offerDetails}
      data-heading={heading}
      data-test="merchant-bonus-offer"
    >
      <div className="mn_merchantBonusTopInfo">
        <div className="mn_ribbon" data-test="merchant-bonus-offer-ribbon">
          <span>{bonusTag}</span>
        </div>
        <h2 data-test="merchant-bonus-offer-heading">{heading}</h2>
        <div className="mn_buttonAndSpentInfoWrap">
          <div className="mn_spentInfoWrap">
            <div className="mn_spentInfo">
              <p className="mn_title">You've spent:</p>
              <p className="mn_info">{spent} of {total}</p>
            </div>
          </div>
          {
            !hasReachedTotal &&
            <a
              className="mn_button mn_secondaryButton mn_shopMoreButton"
              href={clickUrl}
              target="_blank"
              data-test="merchant-bonus-offer-shop-now"
            >
              {buttonTitle}
            </a>
          }
        </div>
      </div>
      <div className="mn_merchantBonusBottomInfo">
        <span className="mn_endInfo">{bonusEndInfo}</span>
        <button
          className="mn_termAndConditionsButton"
          onClick={clickHandler}
          title="View bonus terms and conditions"
        >
          View terms and conditions
        </button>
      </div>
    </div>
  );
}

export default (props) =>
  <ComponentErrorBoundary><MerchantBonusOffer {...props} /></ComponentErrorBoundary>;
